import { useEffect, useState } from "react";
import api from "@/lib/api";
import { Card } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from "@/components/ui/select";
import { formatCurrency, formatNumber } from "@/lib/format";

export default function CustomerAnalysis() {
  const [jaar, setJaar] = useState("alle");
  const [jaren, setJaren] = useState([]);
  const [data, setData] = useState({ klanten: [], klanttype_summary: [] });
  const [loading, setLoading] = useState(true);

  useEffect(() => { api.get("/dashboard/jaren").then(({ data }) => setJaren(data || [])); }, []);

  useEffect(() => {
    setLoading(true);
    const params = {};
    if (jaar && jaar !== "alle") params.jaar = parseInt(jaar);
    api.get("/analytics/customers", { params })
      .then(({ data }) => setData(data))
      .finally(() => setLoading(false));
  }, [jaar]);

  const totaalKlanten = data.klanttype_summary.reduce((a, b) => a + b.totaal_klanten, 0);
  const totaalNieuw = data.klanttype_summary.reduce((a, b) => a + b.nieuw, 0);
  const totaalTerug = data.klanttype_summary.reduce((a, b) => a + b.terugkerend, 0);

  return (
    <div className="space-y-6" data-testid="customers-page">
      <header className="flex flex-wrap items-end justify-between gap-4">
        <div>
          <p className="text-xs tracking-[0.2em] uppercase font-semibold text-slate-500">Klanten</p>
          <h1 className="text-3xl sm:text-4xl tracking-tight font-bold text-slate-900 mt-1">Klantanalyse</h1>
        </div>
        <Select value={jaar} onValueChange={setJaar}>
          <SelectTrigger className="w-32 h-9" data-testid="customers-jaar-select">
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="alle">Alle jaren</SelectItem>
            {jaren.map((y) => <SelectItem key={y} value={String(y)}>{y}</SelectItem>)}
          </SelectContent>
        </Select>
      </header>

      <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
        <Card className="bg-white border-slate-200 p-5 rounded-lg" data-testid="kpi-total-customers">
          <p className="text-xs tracking-[0.2em] uppercase font-semibold text-slate-500">Totaal klanten</p>
          <p className="text-3xl font-semibold tracking-tight text-slate-900 mt-1">{formatNumber(totaalKlanten)}</p>
        </Card>
        <Card className="bg-white border-slate-200 p-5 rounded-lg" data-testid="kpi-new-customers">
          <p className="text-xs tracking-[0.2em] uppercase font-semibold text-slate-500">Nieuwe klanten</p>
          <p className="text-3xl font-semibold tracking-tight text-sky-700 mt-1">{formatNumber(totaalNieuw)}</p>
        </Card>
        <Card className="bg-white border-slate-200 p-5 rounded-lg" data-testid="kpi-returning-customers">
          <p className="text-xs tracking-[0.2em] uppercase font-semibold text-slate-500">Terugkerend</p>
          <p className="text-3xl font-semibold tracking-tight text-emerald-700 mt-1">{formatNumber(totaalTerug)}</p>
        </Card>
      </div>

      <Card className="bg-white border-slate-200 p-6 rounded-lg" data-testid="customer-type-summary">
        <h2 className="text-xl font-semibold text-slate-900 mb-4">Per klanttype</h2>
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="bg-slate-50 border-y border-slate-200 text-slate-600">
                <th className="text-left py-2.5 px-3 font-semibold">Klanttype</th>
                <th className="text-right py-2.5 px-3 font-semibold">Totaal</th>
                <th className="text-right py-2.5 px-3 font-semibold">Nieuw</th>
                <th className="text-right py-2.5 px-3 font-semibold">Terugkerend</th>
              </tr>
            </thead>
            <tbody>
              {data.klanttype_summary.map((s) => (
                <tr key={s._id || s.klanttype} className="border-b border-slate-100">
                  <td className="py-2.5 px-3 text-slate-900">{s._id || s.klanttype}</td>
                  <td className="py-2.5 px-3 text-right tabular-nums">{formatNumber(s.totaal_klanten)}</td>
                  <td className="py-2.5 px-3 text-right tabular-nums text-sky-700">{formatNumber(s.nieuw)}</td>
                  <td className="py-2.5 px-3 text-right tabular-nums text-emerald-700">{formatNumber(s.terugkerend)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </Card>

      <Card className="bg-white border-slate-200 rounded-lg overflow-hidden">
        <div className="p-5 pb-3">
          <h2 className="text-xl font-semibold text-slate-900">Top klanten (op omzet)</h2>
        </div>
        <div className="overflow-x-auto">
          <table className="w-full text-sm" data-testid="customers-table">
            <thead>
              <tr className="bg-slate-50 border-y border-slate-200 text-slate-600">
                <th className="text-left py-3 px-4 font-semibold">Klant</th>
                <th className="text-left py-3 px-4 font-semibold">Type</th>
                <th className="text-left py-3 px-4 font-semibold">Regio</th>
                <th className="text-right py-3 px-4 font-semibold">Orders</th>
                <th className="text-right py-3 px-4 font-semibold">Omzet incl.</th>
                <th className="text-right py-3 px-4 font-semibold">Marge</th>
                <th className="text-right py-3 px-4 font-semibold">Ø order</th>
              </tr>
            </thead>
            <tbody>
              {loading && <tr><td colSpan="7" className="text-center text-slate-400 py-8">Laden…</td></tr>}
              {!loading && data.klanten?.length === 0 && (
                <tr><td colSpan="7" className="text-center text-slate-400 py-8">Geen klanten gevonden.</td></tr>
              )}
              {data.klanten?.map((k) => (
                <tr key={k.email} className="border-b border-slate-100 hover:bg-slate-50/60">
                  <td className="py-2.5 px-4">
                    <div className="font-medium text-slate-900">{k.voornaam} {k.achternaam}</div>
                    <div className="text-xs text-slate-500">{k.bedrijfsnaam || k.email}</div>
                  </td>
                  <td className="py-2.5 px-4">
                    <Badge variant="outline" className="text-xs">{k.klanttype}</Badge>
                    {k.is_terugkerend && <Badge className="ml-1 bg-emerald-50 text-emerald-700 border-emerald-200 text-[10px]">Terug</Badge>}
                  </td>
                  <td className="py-2.5 px-4 text-slate-600">{k.woonplaats} <span className="text-slate-400">· {k.land}</span></td>
                  <td className="py-2.5 px-4 text-right tabular-nums">{formatNumber(k.aantal_orders)}</td>
                  <td className="py-2.5 px-4 text-right tabular-nums text-slate-900">{formatCurrency(k.omzet_incl_btw)}</td>
                  <td className="py-2.5 px-4 text-right tabular-nums text-emerald-700 font-medium">{formatCurrency(k.operationele_marge)}</td>
                  <td className="py-2.5 px-4 text-right tabular-nums text-slate-600">{formatCurrency(k.gemiddelde_orderwaarde)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </Card>
    </div>
  );
}
